import React, { useEffect, useState } from 'react';
import api from '../../../API';
import ItemPicker from '../../../components/ItemPicker';
import SlidePreviewModal from '../SlidePreviewModal';
import RenameResourceModal from '../hub/RenameResourceModal';
import { moveIndex, alphabetizeBy } from '../hub/orderedListUtils';
import ItemGroup from './ItemGroup';
import { buildTree, flattenFolderOptions, groupItemsBySubfolder } from './treeUtils';
import styles from '../ProjectEditForm.module.css';

/**
 * ItemsPanel — project items and subfolders with add/move/delete/rename/reorder.
 * Memoized so it does not re-render when only draft.annotationUI.config changes (e.g. typing in instructions).
 */
function ItemsPanel({
    folderId,
    items,
    subfolders,
    loading,
    error,
    onReload,
    onSetConfirmDelete,
    settingsLocked = false,
    subfolderType = 'project-subfolder',
}) {
    const [checkedItems, setCheckedItems] = useState(new Set());
    const [moveTarget, setMoveTarget] = useState('');
    const [actionError, setActionError] = useState(null);
    const [pickerTargetFolderId, setPickerTargetFolderId] = useState(null);
    const [previewItem, setPreviewItem] = useState(null);
    const [renameTarget, setRenameTarget] = useState(null);
    const [orderBusy, setOrderBusy] = useState(false);

    useEffect(() => {
        if (settingsLocked) {
            setPickerTargetFolderId(null);
            setPreviewItem(null);
            setRenameTarget(null);
        }
    }, [settingsLocked]);

    const subfoldersTree = buildTree(subfolders, folderId);
    const moveOptions = flattenFolderOptions(folderId, subfoldersTree);
    const itemGroups = groupItemsBySubfolder(items);
    const alreadyAdded = new Set(items.map((i) => String(i._id)));

    function toggleCheck(id) {
        setCheckedItems((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }

    function handleAddItems({ itemIds, folderIds }) {
        setPickerTargetFolderId(null);
        setActionError(null);
        const payload = { itemIds, folderIds };
        if (pickerTargetFolderId && pickerTargetFolderId !== folderId) {
            payload.destSubfolderId = pickerTargetFolderId;
        }
        api.post(`projects/${folderId}/items`, { json: payload })
            .then((r) => {
                if (r.status === 200) onReload();
                else setActionError(r.data?.message || 'Failed to add items.');
            })
            .catch((e) => setActionError(e.message));
    }

    function handleBulkMove() {
        if (!moveTarget || checkedItems.size === 0) return;
        setActionError(null);
        api.put(`projects/${folderId}/items/move`, {
            json: { itemIds: Array.from(checkedItems), destFolderId: moveTarget },
        })
            .then((r) => {
                if (r.status === 200) {
                    setCheckedItems(new Set());
                    setMoveTarget('');
                    onReload();
                } else {
                    setActionError(r.data?.message || 'Move failed.');
                }
            })
            .catch((e) => setActionError(e.message));
    }

    function handleSingleMove(itemId, destFolderId) {
        setActionError(null);
        api.put(`projects/${folderId}/items/move`, {
            json: { itemIds: [itemId], destFolderId },
        })
            .then((r) => {
                if (r.status === 200) onReload();
                else setActionError(r.data?.message || 'Move failed.');
            })
            .catch((e) => setActionError(e.message));
    }

    function persistFolderOrder(targetFolderId, orderedItems) {
        if (settingsLocked || orderBusy) return;
        const itemIds = orderedItems.map((it) => String(it._id));
        setOrderBusy(true);
        setActionError(null);
        api.put(`projects/${folderId}/items/order`, {
            json: { folderId: targetFolderId, itemIds },
        })
            .then((r) => {
                if (r.status === 200) onReload();
                else setActionError(r.data?.message || 'Failed to update item order.');
            })
            .catch((e) => setActionError(e.message))
            .finally(() => setOrderBusy(false));
    }

    function handleReorderInFolder(targetFolderId, groupItems, fromIndex, toIndex) {
        const next = moveIndex(groupItems, fromIndex, toIndex);
        persistFolderOrder(targetFolderId, next);
    }

    function handleAlphabetizeFolder(targetFolderId, groupItems) {
        const next = alphabetizeBy(groupItems, (it) => it?.name || '');
        persistFolderOrder(targetFolderId, next);
    }

    function handleCreateSubfolder(parentFolderId, name) {
        return api.post('projects/folders', {
            query: {
                parentId: parentFolderId,
                parentType: 'folder',
                name,
                folderType: subfolderType,
            },
        }).then((r) => {
            if (r.status === 200) onReload();
            else throw new Error(r.data?.message || 'Failed to create subfolder.');
        });
    }

    function deleteSubfolderHandler(id, name) {
        return () => onSetConfirmDelete({ type: 'subfolder', id, name });
    }

    function handleMoveSubfolder(subfolderId, destFolderId) {
        setActionError(null);
        api.put(`projects/${folderId}/folders/${subfolderId}/move`, {
            json: { destFolderId },
        })
            .then((r) => {
                if (r.status === 200) onReload();
                else setActionError(r.data?.message || 'Move failed.');
            })
            .catch((e) => setActionError(e.message));
    }

    function moveSubfolderHandler(subfolderId) {
        return (destId) => handleMoveSubfolder(subfolderId, destId);
    }

    const rootGroupItems = itemGroups[null] || [];

    return (
        <div className={styles.panel}>
            <h4 className={styles.panelTitle}>Image list</h4>
            {settingsLocked && (
                <p className={styles.panelLockBanner}>
                    Published: return to draft to edit this section.
                </p>
            )}

            {actionError && <p className={styles.itemActionError}>{actionError}</p>}
            {loading && <p className={styles.emptyState}>Loading images…</p>}
            {error && <p className={styles.itemActionError}>{error}</p>}

            {checkedItems.size > 0 && (
                <div className={styles.bulkBar}>
                    <span className={styles.bulkCount}>{checkedItems.size} selected</span>
                    <select
                        className={styles.bulkMoveSelect}
                        value={moveTarget}
                        onChange={(e) => setMoveTarget(e.target.value)}
                    >
                        <option value="">Move to…</option>
                        {moveOptions.map((opt) => (
                            <option key={opt.id} value={opt.id}>{opt.name}</option>
                        ))}
                    </select>
                    <button
                        type="button"
                        className={styles.bulkMoveButton}
                        onClick={handleBulkMove}
                        disabled={!moveTarget}
                    >
                        Move
                    </button>
                </div>
            )}

            {!loading && !error && (
                <div className={settingsLocked ? styles.panelDisabled : undefined}>
                    <ItemGroupTree
                        folderId={folderId}
                        projectFolderId={folderId}
                        rootLabel={subfolderType === 'slideset-subfolder' ? '(Slide Set root)' : '(project root)'}
                        rootGroupItems={rootGroupItems}
                        subfoldersTree={subfoldersTree}
                        itemGroups={itemGroups}
                        checkedItems={checkedItems}
                        onToggle={toggleCheck}
                        moveOptions={moveOptions}
                        onSingleMove={handleSingleMove}
                        onDeleteItem={(id, name) => onSetConfirmDelete({ type: 'item', id, name })}
                        deleteSubfolderHandler={deleteSubfolderHandler}
                        moveSubfolderHandler={moveSubfolderHandler}
                        onAddItems={(targetFolderId) => setPickerTargetFolderId(targetFolderId)}
                        onCreateSubfolder={handleCreateSubfolder}
                        onUploadDone={onReload}
                        onPreviewItem={(id, name) => setPreviewItem({ id, name })}
                        onRenameItem={(id, name) => setRenameTarget({ itemId: id, initialName: name })}
                        onReorderInFolder={handleReorderInFolder}
                        onAlphabetizeFolder={handleAlphabetizeFolder}
                        orderBusy={orderBusy}
                        settingsLocked={settingsLocked}
                    />
                </div>
            )}

            {pickerTargetFolderId && !settingsLocked && (
                <ItemPicker
                    onConfirm={handleAddItems}
                    onCancel={() => setPickerTargetFolderId(null)}
                    alreadyAdded={alreadyAdded}
                />
            )}

            {previewItem && !settingsLocked && (
                <SlidePreviewModal
                    itemId={previewItem.id}
                    itemName={previewItem.name}
                    onClose={() => setPreviewItem(null)}
                />
            )}

            <RenameResourceModal
                open={!!renameTarget}
                projectFolderId={folderId}
                itemId={renameTarget?.itemId ?? null}
                initialName={renameTarget?.initialName ?? ''}
                title="Rename item"
                onClose={() => setRenameTarget(null)}
                onSuccess={() => {
                    setRenameTarget(null);
                    onReload();
                }}
            />
        </div>
    );
}

export default React.memo(ItemsPanel);

/**
 * ItemGroupTree — root ItemGroup with full subfolder tree as childFolders.
 */
function ItemGroupTree({
    folderId,
    projectFolderId,
    rootLabel = '(project root)',
    rootGroupItems,
    subfoldersTree,
    itemGroups,
    checkedItems,
    onToggle,
    moveOptions,
    onSingleMove,
    onDeleteItem,
    deleteSubfolderHandler,
    moveSubfolderHandler,
    onAddItems,
    onCreateSubfolder,
    onUploadDone,
    onPreviewItem,
    onRenameItem,
    onReorderInFolder,
    onAlphabetizeFolder,
    orderBusy,
    settingsLocked,
}) {
    return (
        <ItemGroup
            folderId={folderId}
            projectFolderId={projectFolderId}
            label={rootLabel}
            groupItems={rootGroupItems}
            childFolders={subfoldersTree}
            depth={0}
            checkedItems={checkedItems}
            onToggle={onToggle}
            onToggleAll={() => {
                const ids = rootGroupItems.map((i) => String(i._id));
                const allChecked = ids.length > 0 && ids.every((id) => checkedItems.has(id));
                ids.forEach((id) => {
                    if (allChecked ? checkedItems.has(id) : !checkedItems.has(id)) {
                        onToggle(id);
                    }
                });
            }}
            moveOptions={moveOptions}
            onSingleMove={onSingleMove}
            onDeleteItem={onDeleteItem}
            onAddItems={onAddItems}
            onCreateSubfolder={onCreateSubfolder}
            onUploadDone={onUploadDone}
            onPreviewItem={onPreviewItem}
            onRenameItem={onRenameItem}
            onReorderInFolder={onReorderInFolder}
            onAlphabetizeFolder={onAlphabetizeFolder}
            orderBusy={orderBusy}
            settingsLocked={settingsLocked}
            itemGroups={itemGroups}
            deleteSubfolderHandler={deleteSubfolderHandler}
            moveSubfolderHandler={moveSubfolderHandler}
            subfoldersTree={subfoldersTree}
        />
    );
}
